import { z } from "zod";

/**
 * Time-range presets exposed in the dashboard filter bar. `custom` requires
 * `customStart` and `customEnd` to be provided; the schema's `.refine()`
 * enforces this.
 */
export const TIME_RANGE_PRESETS = [
	"mtd",
	"qtd",
	"ytd",
	"last_30d",
	"last_90d",
	"last_12mo",
	"last_24mo",
	"custom",
] as const;
export type TimeRangePreset = (typeof TIME_RANGE_PRESETS)[number];

/**
 * Which invoice date column the time window applies to.
 * - `period_end`: invoice billing-period end date (TEXT column `YYYY-MM-DD`).
 * - `created_at`: timestamp the invoice row was created.
 */
export const TIME_BASES = ["period_end", "created_at"] as const;
export type TimeBasis = (typeof TIME_BASES)[number];

/** Customer status filter — defaults to active so churned customers don't pollute the dashboard. */
export const CUSTOMER_STATUSES = ["active", "inactive", "all"] as const;
export type CustomerStatusFilter = (typeof CUSTOMER_STATUSES)[number];

/**
 * Payroll providers selectable in the filter bar. This is intentionally a
 * subset of `PAYROLL_SYSTEM_STORAGE_VALUES` — the dashboard never filters by
 * the transient `unknown` sentinel, and "ADP WFN" is folded into "ADP" for
 * the purposes of dashboard scoping.
 */
export const PAYROLL_PROVIDERS = ["ADP", "Paylocity", "SurePayroll", "Gusto"] as const;
export type PayrollProvider = (typeof PAYROLL_PROVIDERS)[number];

/**
 * Billing-mode filter buckets. `pass_through` is a separate boolean column on
 * customers, not a value of `billingMode`, so we model it as its own bucket
 * here and the service helper translates appropriately.
 */
export const BILLING_MODES_FILTER = ["per_pay", "monthly", "pass_through"] as const;
export type BillingModeFilter = (typeof BILLING_MODES_FILTER)[number];

/**
 * Wire shape for every dashboard query input. All fields have defaults so a
 * caller passing `undefined` (e.g. on first render before search params are
 * parsed) still gets a sane query. Custom range is enforced by `.refine()`
 * — both dates must accompany a `timeRange === "custom"` selection.
 */
export const dashboardFiltersSchema = z
	.object({
		timeRange: z.enum(TIME_RANGE_PRESETS).default("ytd"),
		// Required iff timeRange === "custom". Both must be present together.
		customStart: z.string().date().optional(),
		customEnd: z.string().date().optional(),
		timeBasis: z.enum(TIME_BASES).default("period_end"),
		providers: z.array(z.enum(PAYROLL_PROVIDERS)).default([]),
		billingModes: z.array(z.enum(BILLING_MODES_FILTER)).default([]),
		customerStatus: z.enum(CUSTOMER_STATUSES).default("active"),
		customerIds: z.array(z.string()).default([]),
		// Widget-specific knobs that previously lived on individual procedure
		// inputs (`revenueTrendInputSchema.months`, `customerConcentrationInputSchema.topN`).
		// Defaults preserve current behavior for callers passing no override.
		// `trendMonths` is vestigial as of 2026-05-04: the chart now derives
		// its month count from `timeRange` via `chartMonthsForFilters`. Field
		// retained on the schema to avoid a wider blast; remove in a future cleanup.
		trendMonths: z.number().int().min(1).max(36).default(12),
		concentrationTopN: z.number().int().min(1).max(20).default(5),
	})
	.refine((v) => v.timeRange !== "custom" || (v.customStart != null && v.customEnd != null), {
		message: "customStart and customEnd are required when timeRange is 'custom'",
		path: ["customStart"],
	});

export type DashboardFilters = z.infer<typeof dashboardFiltersSchema>;
